import { Product } from "@omni/types/api/product/bff/product.model";
import { ProductFieldSet } from "@omni/types/api/product/bff/shared/enums/productFieldSet.enum";
import { ProductVisibilityType } from "@omni/types/api/product/bff/shared/enums/productVisibilityType.enum";
import { AlgoliaSearchAutocompleteProduct } from "@omni/types/api/search/algolia/product/autocompleteProduct.model";
import { AlgoliaSearchProduct } from "@omni/types/api/search/algolia/product/product.model";
import { AlgoliaSearchProductVisibilityType } from "@omni/types/api/search/algolia/product/shared/enums/productVisibilityType.enum";
import { BffSearchProduct } from "@omni/types/api/search/bff/product/product.model";
import { BffSearchProductVisibilityType } from "@omni/types/api/search/bff/product/shared/enums/productVisibilityType.enum";
import { ModelMapper } from "@omni/types/utils/modelMapper";

import { DeliveryTypeMapper } from "@/product/shared/enums/deliveryType.enumMapper";
import { ProductVisibilityTypeMapper } from "@/product/shared/enums/productVisibilityType.enumMapper";
import { FeatureMapper } from "@/product/shared/feature.mapper";
import { getMedias } from "@/product/shared/media.mapper";
import { PriceMapper } from "@/product/shared/price.mapper";
import { PromoLabelMapper } from "@/product/shared/promoLabel.mapper";
import { SalesIncentiveMapper } from "@/product/shared/salesIncentive.mapper";
import { getTitle } from "@/product/shared/title.mapper";

const fromFullToProductTile: ModelMapper<
    Product<ProductFieldSet.Full>,
    Product<ProductFieldSet.ProductTile>
> = (model) => {
    const title = getTitle(model);

    const medias = getMedias(
        false,
        title,
        model.mainImages,
        model.articleConfiguration
    );

    const salesIncentives = model.salesIncentives?.sort((a, b) =>
        a.order > b.order ? 1 : -1
    );

    return {
        analytics: model.analytics,
        articleConfiguration: model.articleConfiguration,
        baseStore: model.baseStore,
        brand: model.brand,
        boxArticle: model.boxArticle,
        deliveryTypes: model.deliveryTypes || [],
        description: model.description,
        disablePDPLink:
            model.productVisibilityType === ProductVisibilityType.Offline,
        ean: model.ean,
        ecochecksAcceptedAsPayment: model.ecochequesAccepted,
        hasInstallationService: (model.references?.montages || []).length > 0,
        features: model.features,
        isAdditionalService: model.montageParents.length > 0,
        medias: medias,
        mainImages: model.mainImages,
        minimumOrderQuantity: model.minimumOrderQuantity || 1,
        name: model.name,
        price: PriceMapper.fromBffToProductTile(model.price),
        productTitle: model.productTitle,
        productVisibilityType: model.productVisibilityType,
        promoLabel: model.promoLabel,
        salesIncentives: salesIncentives,
        sku: model.sku,
        statistics: model.statistics,
        superCategoryName: model.superCategoryName,
        superCategoryUrl: model.superCategoryUrl,
        title: title,
        url: model.articleConfiguration ? undefined : model.url,
        showConvertedPriceAsMainPrice: model.showConvertedPriceAsMainPrice,
        eprelRegistrationNumber: model.eprelRegistrationNumber || undefined,
        warrantyDocuments: model.warrantyDocuments || []
    } satisfies Product<ProductFieldSet.ProductTile>;
};

export const fromAlgoliaSearchToProductTile: ModelMapper<
    AlgoliaSearchProduct,
    Product<ProductFieldSet.ProductTile>
> = (model) => {
    const title = getTitle(model);

    const medias = getMedias(false, title, model.mainImages);

    const promoLabel = PromoLabelMapper.fromAlgoliaSearchToBff({
        salesIncentives: model.salesIncentives,
        discountPercentage: model.price?.discountPercentage ?? null,
        promotions: model.price?.promotions ?? []
    });

    return {
        ean: model.ean,
        sku: model.sku,
        brand: {
            name: model.brand || "",
            code: "",
            image: ""
        },
        disablePDPLink:
            model.productVisibilityType ===
            AlgoliaSearchProductVisibilityType.Offline,
        ecochecksAcceptedAsPayment: model.ecochecksAcceptedAsPayment,
        hasInstallationService: model.hasInstallationService ?? false,
        title: title,
        productVisibilityType:
            ProductVisibilityTypeMapper.fromAlgoliaSearchToBff(
                model.productVisibilityType
            ),
        url: model.url,
        deliveryTypes: model.deliveryTypes.map(
            DeliveryTypeMapper.fromAlgoliaSearchToBff
        ),
        minimumOrderQuantity: model.minimumOrderQuantity || 1,
        medias: medias,
        features: model.features.map(FeatureMapper.fromBffSearchToProductTile),
        superCategoryName: model.categoryName || "",
        superCategoryUrl: model.categoryUrl,
        price: PriceMapper.fromAlgoliaSearchToProductTile(model.price),
        salesIncentives: model.salesIncentives.map(
            SalesIncentiveMapper.fromAlgoliaSearchToBff
        ),
        baseStore: model.baseStore,
        description: model.description,
        mainImages: model.mainImages,
        name: model.name,
        productTitle: model.productTitle,
        articleConfiguration: null,
        analytics: {
            productTitle: model.productTitle || model.name || "",
            superCategoryCode: model.categoryName || "",
            superCategoryName: model.categoryName?.toLowerCase() || ""
        },
        // Additional services aren't exported to Algolia so this product cannot be an additional service
        isAdditionalService: false,
        boxArticle: model.boxArticle,
        promoLabel: promoLabel,
        showConvertedPriceAsMainPrice: false,
        eprelRegistrationNumber: model.eprelRegistrationNumber || undefined,
        warrantyDocuments: model.warrantyDocuments || []
    } satisfies Product<ProductFieldSet.ProductTile>;
};

export const fromAlgoliaSearchAutocompleteToProductTile: ModelMapper<
    AlgoliaSearchAutocompleteProduct,
    Product<ProductFieldSet.ProductTile>
> = (model) => {
    const title = getTitle(model);

    const medias = getMedias(false, title, model.mainImages);

    const promoLabel = PromoLabelMapper.fromAlgoliaSearchToBff({
        salesIncentives: model.salesIncentives,
        discountPercentage: model.price?.discountPercentage ?? null,
        promotions: model.price?.promotions ?? []
    });

    return {
        ean: model.ean,
        sku: model.sku,
        brand: {
            name: model.brand || "",
            code: "",
            image: ""
        },
        disablePDPLink:
            model.productVisibilityType ===
            AlgoliaSearchProductVisibilityType.Offline,
        ecochecksAcceptedAsPayment: model.ecochecksAcceptedAsPayment,
        hasInstallationService: model.hasInstallationService ?? false,
        title: title,
        productVisibilityType:
            ProductVisibilityTypeMapper.fromAlgoliaSearchToBff(
                model.productVisibilityType
            ),
        url: model.url,
        deliveryTypes: model.deliveryTypes.map(
            DeliveryTypeMapper.fromAlgoliaSearchToBff
        ),
        minimumOrderQuantity: model.minimumOrderQuantity || 1,
        medias: medias,
        features: model.features?.map(
            FeatureMapper.fromAlgoliaSearchToProductTile
        ),
        superCategoryName: model.categoryName || "",
        superCategoryUrl: model.categoryUrl,
        price: PriceMapper.fromAlgoliaSearchToProductTile(model.price),
        salesIncentives: model.salesIncentives.map(
            SalesIncentiveMapper.fromAlgoliaSearchToBff
        ),
        baseStore: model.baseStore,
        description: model.description,
        mainImages: model.mainImages,
        name: model.name,
        productTitle: model.productTitle,
        articleConfiguration: null,
        analytics: {
            productTitle: model.productTitle || model.name || "",
            superCategoryCode: model.categoryName || "",
            superCategoryName: model.categoryName?.toLowerCase() || ""
        },
        // Additional services aren't exported to Algolia so this product cannot be an additional service
        isAdditionalService: false,
        boxArticle: model.boxArticle,
        promoLabel: promoLabel,
        showConvertedPriceAsMainPrice: false,
        statistics: {
            averageOverallRating: model.averageOverallRating || 0,
            totalReviewCount: model.totalReviewCount || 0
        },
        eprelRegistrationNumber: model.eprelRegistrationNumber || undefined,
        warrantyDocuments: model.warrantyDocuments || []
    } satisfies Product<ProductFieldSet.ProductTile>;
};

export const fromBffSearchToProductTile: ModelMapper<
    BffSearchProduct,
    Product<ProductFieldSet.ProductTile>
> = (model) => {
    const title = getTitle(model);

    const medias = getMedias(false, title, model.mainImages);

    const promoLabel = PromoLabelMapper.fromBffSearchToBff({
        salesIncentives: model.salesIncentives,
        discountPercentage: model.price?.discountPercentage ?? null,
        promotions: model.price?.promotions ?? []
    });

    return {
        ean: model.ean,
        sku: model.sku,
        brand: {
            name: model.brand || "",
            code: "",
            image: ""
        },
        disablePDPLink:
            model.productVisibilityType ===
            BffSearchProductVisibilityType.Offline,
        ecochecksAcceptedAsPayment: model.ecochecksAcceptedAsPayment,
        hasInstallationService: model.hasInstallationService,
        title: title,
        productVisibilityType: ProductVisibilityTypeMapper.fromBffSearchToBff(
            model.productVisibilityType
        ),
        url: model.url,
        deliveryTypes: model.deliveryTypes.map(
            DeliveryTypeMapper.fromBffSearchToBff
        ),
        minimumOrderQuantity: model.minimumOrderQuantity || 1,
        medias: medias,
        features: model.features.map(FeatureMapper.fromBffSearchToProductTile),
        superCategoryName: model.categoryName || "",
        superCategoryUrl: model.categoryUrl,
        price: PriceMapper.fromBffSearchToProductTile(model.price),
        salesIncentives: model.salesIncentives.map(
            SalesIncentiveMapper.fromBffSearchToBff
        ),
        baseStore: model.baseStore,
        description: model.description,
        mainImages: model.mainImages,
        name: model.name,
        productTitle: model.productTitle,
        articleConfiguration: null,
        analytics: {
            productTitle: model.productTitle || model.name || "",
            superCategoryCode: model.categoryName || "",
            superCategoryName: model.categoryName?.toLowerCase() || ""
        },
        // Additional services aren't exported to Algolia so this product cannot be an additional service
        isAdditionalService: false,
        boxArticle: model.boxArticle,
        promoLabel: promoLabel,
        statistics: model.statistics,
        showConvertedPriceAsMainPrice: model.showConvertedPriceAsMainPrice,
        eprelRegistrationNumber: model.eprelRegistrationNumber || undefined,
        warrantyDocuments: model.warrantyDocuments || []
    } satisfies Product<ProductFieldSet.ProductTile>;
};

export const fromBffSearchToAlgoliaSearch: ModelMapper<
    BffSearchProduct,
    AlgoliaSearchProduct
> = (model) => {
    return {
        objectID: model.sku,
        ean: model.ean,
        sku: model.sku,
        brand: model.brand,
        ecochecksAcceptedAsPayment: model.ecochecksAcceptedAsPayment,
        hasInstallationService: model.hasInstallationService,
        productVisibilityType:
            ProductVisibilityTypeMapper.fromBffSearchToAlgoliaSearch(
                model.productVisibilityType
            ),
        url: model.url,
        deliveryTypes: model.deliveryTypes.map(
            DeliveryTypeMapper.fromBffSearchToAlgoliaSearch
        ),
        minimumOrderQuantity: model.minimumOrderQuantity || 1,
        features: model.features.map(
            FeatureMapper.fromAlgoliaSearchToBffSearch
        ),
        categoryName: model.categoryName,
        categoryUrl: model.categoryUrl,
        price: PriceMapper.fromBffSearchToAlgoliaSearch(model.price),
        salesIncentives: model.salesIncentives.map(
            SalesIncentiveMapper.fromBffSearchToAlgoliaSearch
        ),
        baseStore: model.baseStore,
        description: model.description,
        mainImages: model.mainImages ?? [],
        name: model.name,
        productTitle: model.productTitle,
        // Additional services aren't exported to Algolia so this product cannot be an additional service
        boxArticle: model.boxArticle,
        averageOverallRating: model.statistics?.averageOverallRating || 0,
        totalReviewCount: model.statistics?.totalReviewCount || 0,
        eprelRegistrationNumber: model.eprelRegistrationNumber || undefined,
        warrantyDocuments: model.warrantyDocuments || []
    } satisfies AlgoliaSearchProduct;
};

const fromProductTileToAlgoliaSearch: ModelMapper<
    Product<ProductFieldSet.ProductTile>,
    AlgoliaSearchProduct
> = (model) => {
    return {
        objectID: model.sku,
        sku: model.sku,
        baseStore: model.baseStore,
        boxArticle: model.boxArticle,
        description: model.description || "",
        name: model.name || "",
        productTitle: model.productTitle || "",
        url: model.url || "",
        features: model.features.map(
            FeatureMapper.fromProductTileToAlgoliaSearch
        ),
        mainImages: model.mainImages || [],
        categoryName: model.superCategoryName || "",
        categoryUrl: model.superCategoryUrl || "",
        minimumOrderQuantity: model.minimumOrderQuantity || 0,
        brand: model.brand?.name || "",
        deliveryTypes: model.deliveryTypes.map(
            DeliveryTypeMapper.fromBffToAlgoliaSearch
        ),
        productVisibilityType:
            ProductVisibilityTypeMapper.fromBffToAlgoliaSearch(
                model.productVisibilityType
            ),
        ecochecksAcceptedAsPayment: model.ecochecksAcceptedAsPayment || false,
        hasInstallationService: model.hasInstallationService || false,
        salesIncentives:
            model.salesIncentives?.map(
                SalesIncentiveMapper.fromBffToAlgoliaSearch
            ) ?? [],
        ean: model.ean || "",
        price: PriceMapper.fromProductTileToAlgoliaSearch(model.price),
        averageOverallRating: model.statistics?.averageOverallRating || 0,
        totalReviewCount: model.statistics?.totalReviewCount || 0,
        eprelRegistrationNumber: model.eprelRegistrationNumber || undefined,
        warrantyDocuments: model.warrantyDocuments || []
    } satisfies AlgoliaSearchProduct;
};

const fromProductTileToBffSearch: ModelMapper<
    Product<ProductFieldSet.ProductTile>,
    BffSearchProduct
> = (model) => {
    return {
        sku: model.sku,
        baseStore: model.baseStore,
        boxArticle: model.boxArticle,
        description: model.description || "",
        statistics: model.statistics,
        name: model.name || "",
        productTitle: model.productTitle || "",
        url: model.url || "",
        features: model.features.map(FeatureMapper.fromProductTileToBffSearch),
        mainImages: model.mainImages || [],
        categoryName: model.superCategoryName || "",
        categoryUrl: model.superCategoryUrl || "",
        minimumOrderQuantity: model.minimumOrderQuantity || 0,
        brand: model.brand?.name || "",
        deliveryTypes: model.deliveryTypes.map(
            DeliveryTypeMapper.fromBffToBffSearch
        ),
        productVisibilityType: ProductVisibilityTypeMapper.fromBffToBffSearch(
            model.productVisibilityType
        ),
        ecochecksAcceptedAsPayment: model.ecochecksAcceptedAsPayment || false,
        hasInstallationService: model.hasInstallationService || false,
        salesIncentives:
            model.salesIncentives?.map(
                SalesIncentiveMapper.fromBffToBffSearch
            ) ?? [],
        ean: model.ean || "",
        showConvertedPriceAsMainPrice: false,
        price: PriceMapper.fromProductTileToBffSearch(model.price),
        objectID: model.sku,
        eprelRegistrationNumber: model.eprelRegistrationNumber || undefined,
        warrantyDocuments: model.warrantyDocuments || []
    } satisfies BffSearchProduct;
};

export const ProductMapper = {
    fromFullToProductTile: fromFullToProductTile,
    fromAlgoliaSearchToProductTile: fromAlgoliaSearchToProductTile,
    fromAlgoliaSearchAutocompleteToProductTile:
        fromAlgoliaSearchAutocompleteToProductTile,
    fromBffSearchToProductTile: fromBffSearchToProductTile,
    fromBffSearchToAlgoliaSearch: fromBffSearchToAlgoliaSearch,
    fromProductTileToAlgoliaSearch: fromProductTileToAlgoliaSearch,
    fromProductTileToBffSearch: fromProductTileToBffSearch
};
